import { useState, useEffect } from 'react';
import { Calendar, Clock, Truck, AlertTriangle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DriverSchedulesView = () => {
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLog, setActiveLog] = useState(null);
  const [dateView, setDateView] = useState('today'); // 'today', 'tomorrow', 'custom'
  const [customDate, setCustomDate] = useState('');

  useEffect(() => {
    fetchMySchedules();
  }, []);

  const fetchMySchedules = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/driver-schedules/my-schedules`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch schedules');
      }

      const data = await response.json();
      setSchedules(data.schedules || []);
      setActiveLog(data.active_log);
    } catch (error) {
      toast.error('Failed to load schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleClockIn = async (scheduleId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/driver-schedules/${scheduleId}/clock-in`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to clock in');
      }

      const data = await response.json();
      toast.success('Clocked in successfully');
      
      // Navigate to driver log
      navigate('/profile/driver-log', { state: { logId: data.log_id, scheduleId } });
    } catch (error) {
      toast.error('Failed to clock in');
    }
  };

  const getFilteredSchedules = () => {
    let targetDate;
    
    if (dateView === 'today') {
      targetDate = new Date().toISOString().split('T')[0];
    } else if (dateView === 'tomorrow') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      targetDate = tomorrow.toISOString().split('T')[0];
    } else if (dateView === 'custom' && customDate) {
      targetDate = customDate;
    } else {
      return [];
    }
    
    return schedules.filter(s => s.schedule_date === targetDate);
  };

  const filteredSchedules = getFilteredSchedules();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 py-6 sm:py-8 px-4 sm:px-6 overflow-x-hidden">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/profile')} variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Schedules</h1>
              <p className="text-sm text-gray-500">View and clock in to your scheduled routes</p>
            </div>
          </div>
        </div>

        {/* Date Filter Options */}
        <div className="mb-6 bg-white rounded-xl shadow-md p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3 items-stretch sm:items-center">
            <Button
              onClick={() => setDateView('today')}
              className={`${
                dateView === 'today' 
                  ? 'bg-teal-600 hover:bg-teal-700 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              Today
            </Button>
            <Button
              onClick={() => setDateView('tomorrow')}
              className={`${
                dateView === 'tomorrow' 
                  ? 'bg-teal-600 hover:bg-teal-700 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              Tomorrow
            </Button>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <Button
                onClick={() => setDateView('custom')}
                className={`${
                  dateView === 'custom' 
                    ? 'bg-teal-600 hover:bg-teal-700 text-white' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}
              >
                Custom Date
              </Button>
              {dateView === 'custom' && (
                <input
                  type="date"
                  value={customDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                  className="h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 w-full sm:w-auto"
                />
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading schedules...</p>
          </div>
        ) : filteredSchedules.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-md">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              No schedules for {dateView === 'today' ? 'today' : dateView === 'tomorrow' ? 'tomorrow' : 'selected date'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredSchedules.map((schedule) => (
              <div
                key={schedule.id}
                className={`bg-white rounded-xl shadow-md p-4 sm:p-6 ${
                  schedule.status === 'live' ? 'border-2 border-green-500' :
                  schedule.status === 'completed' ? 'border-2 border-gray-400' :
                  'border-2 border-blue-500'
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                        {new Date(schedule.schedule_date).toLocaleDateString("en-GB")}
                      </h3>
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        schedule.status === 'live' ? 'bg-green-100 text-green-700' :
                        schedule.status === 'completed' ? 'bg-gray-100 text-gray-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {schedule.status === 'created' ? 'Ready' : schedule.status}
                      </span>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-gray-700 text-sm sm:text-base">
                        <Clock className="w-4 h-4 flex-shrink-0" />
                        <span className="break-words">{schedule.start_time} - {schedule.finish_time}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-700 text-sm sm:text-base">
                        <Truck className="w-4 h-4 flex-shrink-0" />
                        <span className="break-words">{schedule.vehicle_registration} ({schedule.vehicle_type})</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex-shrink-0 w-full sm:w-auto">
                    {schedule.status === 'created' ? (
                      <Button
                        onClick={() => handleClockIn(schedule.id)}
                        className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                      >
                        Clock In
                      </Button>
                    ) : schedule.status === 'live' ? (
                      <Button
                        onClick={() => navigate('/profile/driver-log', { state: { logId: schedule.log_id, scheduleId: schedule.id } })}
                        className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
                      >
                        Update Driver Log
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        disabled
                        className="w-full sm:w-auto"
                      >
                        Completed
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverSchedulesView;
